// src/services/auth/jwt-auth/index.js
import axios from 'axios';
import {API_CONFIG} from '../../../../config/api.config';

const TIMEZONE_KEY = 'tz';

export const jwtAxios = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** ---------- Timezone helpers ---------- */
export const getClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

export const getClientTzOffsetMinutes = () => {
  try {
    // JS offset is minutes *behind* UTC; invert so UTC+2 => 120
    return -new Date().getTimezoneOffset();
  } catch {
    return null;
  }
};

export const primeLocalTimezone = () => {
  const tz = getClientTimezone();
  if (tz) localStorage.setItem(TIMEZONE_KEY, tz);
  return tz;
};

/** ---------- Interceptors ---------- */
// Attach timezone and language headers to all requests
jwtAxios.interceptors.request.use((config) => {
  const tz = localStorage.getItem(TIMEZONE_KEY) || getClientTimezone();
  const off = getClientTzOffsetMinutes();
  if (tz) config.headers['x-timezone'] = tz;
  if (typeof off === 'number') config.headers['x-tz-offset-min'] = String(off);

  // Add Accept-Language header based on current locale
  let locale = 'en';
  try {
    // First try to get from simple locale storage
    const simpleLocale = localStorage.getItem('locale');
    if (simpleLocale) {
      locale = simpleLocale;
    } else {
      // Fallback to complex locale object
      const localeObj = localStorage.getItem('app_locale');
      if (localeObj) {
        const parsed = JSON.parse(localeObj);
        locale = parsed.locale || 'en';
      }
    }
  } catch (e) {
    locale = 'en';
  }
  config.headers['Accept-Language'] = locale === 'ar' ? 'ar' : 'en';

  // If data is FormData, remove Content-Type header to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    // Also delete from common headers if it exists
    delete config.headers.common['Content-Type'];
    // Ensure we're not overriding it
    if (config.headers && config.headers['Content-Type']) {
      delete config.headers['Content-Type'];
    }
  }

  // Ensure Authorization header is preserved
  const token = localStorage.getItem('token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = 'Bearer ' + token;
  }

  return config;
});

// Keep response interceptor minimal; auth flow handles refresh/logout.
jwtAxios.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err),
);

/** ---------- Auth header helper ---------- */
export const setAuthToken = (token) => {
  if (token) {
    jwtAxios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    localStorage.setItem('token', token);

    // Also update any other axios instances that might be using the token
    // This ensures all API calls use the refreshed token

    // Trigger a custom event to notify other parts of the app about token update
    window.dispatchEvent(new CustomEvent('tokenUpdated', {detail: {token}}));
  } else {
    delete jwtAxios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');

    // Trigger a custom event to notify other parts of the app about token removal
    window.dispatchEvent(new CustomEvent('tokenRemoved'));
  }
};

/**
 * Idempotent helper to sync the browser's timezone into the user's profile.
 * Safe to call after login, refresh, and app bootstrap.
 * Backend route expected: PATCH /api/auth/me  (body: { timezone })
 */
export const syncTimezoneProfile = async () => {
  const tz = primeLocalTimezone() || getClientTimezone();
  if (!tz) return;
  try {
    await jwtAxios.patch('/api/auth/me', {timezone: tz});
  } catch (error) {
    // Non-fatal if unauthenticated or endpoint unavailable
  }
};

// Response interceptor to handle 401 errors
jwtAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('🔐 401 Unauthorized - clearing auth data');

      // Clear tokens from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authPayload');

      // Clear auth token from axios
      setAuthToken(null);

      // Let React Router handle the redirect securely instead of hard-reloading
      window.dispatchEvent(new CustomEvent('unauthorized'));
    }

    return Promise.reject(error);
  },
);

export default jwtAxios;
